import React, { useState, useEffect } from 'react';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WorkspaceProvider, useWorkspace } from './context/WorkspaceContext';
import { ModuleProvider } from './context/ModuleContext';
import { ToastProvider } from './design-system/Toast';
import { AppShell } from './components/layout/AppShell';

import { LandingPage } from './pages/landing/LandingPage';
import { LoginPage } from './pages/auth/LoginPage';
import { OnboardingPage } from './pages/onboarding/OnboardingPage';
import { WorkspaceHome } from './pages/workspace/WorkspaceHome';
import { MyWorkPage } from './pages/workspace/MyWorkPage';
import { InboxPage } from './pages/workspace/InboxPage';
import { MembersPage } from './pages/workspace/MembersPage';
import { ProjectsDirectoryPage } from './pages/project/ProjectsDirectoryPage';
import { ProjectView } from './pages/project/ProjectView';
import { ModuleLibraryPage } from './pages/modules/ModuleLibraryPage';
import { GlobalSearchPage } from './pages/search/GlobalSearchPage';
import { PublicProjectPage } from './pages/public/PublicProjectPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { Layers } from 'lucide-react';

const MainAppRouter = () => {
  const { isAuthenticated, isAuthenticating, loginWithLazplay } = useAuth();
  const { workspaces } = useWorkspace();

  // If user is authenticated, default to workspace_home or onboarding
  const [currentView, setCurrentView] = useState(() => {
    if (isAuthenticated) {
      return workspaces.length === 0 ? 'onboarding' : 'workspace_home';
    }
    return 'landing';
  });
  const [currentProjectView, setCurrentProjectView] = useState('board');

  // Automatically transition view on auth state changes
  useEffect(() => {
    if (isAuthenticated) {
      if (currentView === 'landing' || currentView === 'login') {
        setCurrentView(workspaces.length === 0 ? 'onboarding' : 'workspace_home');
      }
    } else {
      if (!['landing', 'login', 'public_portal'].includes(currentView)) {
        setCurrentView('landing');
      }
    }
  }, [isAuthenticated, workspaces.length, currentView]);

  // Loading state while exchanging PKCE tokens with Lazplay Accounts IdP
  if (isAuthenticating) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: 'var(--bg-app)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px'
        }}
      >
        <div
          style={{
            width: '54px',
            height: '54px',
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--accent-container)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            boxShadow: 'var(--shadow-raised)',
            animation: 'pulse 1.5s infinite ease-in-out'
          }}
        >
          <Layers size={28} strokeWidth={2.5} />
        </div>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '800' }}>Authenticating with Lazplay Accounts...</h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Fetching verified identity and profile metadata
          </p>
        </div>
      </div>
    );
  }

  // If unauthenticated and on an internal view, redirect to landing
  const isAuthRestrictedView = ![
    'landing',
    'login',
    'public_portal'
  ].includes(currentView);

  // Unauthenticated Views Routing
  if (!isAuthenticated && isAuthRestrictedView) {
    return (
      <LandingPage
        onSignIn={loginWithLazplay}
        onGetStarted={loginWithLazplay}
        onExploreDemo={() => setCurrentView('workspace_home')}
      />
    );
  }

  if (currentView === 'landing') {
    return (
      <LandingPage
        onSignIn={loginWithLazplay}
        onGetStarted={loginWithLazplay}
        onExploreDemo={() => setCurrentView('workspace_home')}
      />
    );
  }

  if (currentView === 'public_portal') {
    return <PublicProjectPage onBackToApp={() => setCurrentView(isAuthenticated ? 'workspace_home' : 'landing')} />;
  }

  if (currentView === 'login') {
    return (
      <LoginPage
        onLoginSuccess={() => setCurrentView(workspaces.length === 0 ? 'onboarding' : 'workspace_home')}
      />
    );
  }

  if (currentView === 'onboarding') {
    return <OnboardingPage onComplete={() => setCurrentView('workspace_home')} />;
  }

  // Authenticated In-App Experience wrapped in AppShell
  const renderCurrentView = () => {
    switch (currentView) {
      case 'my_work':
        return <MyWorkPage />;
      case 'inbox':
        return <InboxPage />;
      case 'members':
        return <MembersPage />;
      case 'projects_directory':
        return (
          <ProjectsDirectoryPage
            setCurrentView={setCurrentView}
            setCurrentProjectView={setCurrentProjectView}
          />
        );
      case 'project_view':
        return (
          <ProjectView
            currentProjectView={currentProjectView}
            setCurrentProjectView={setCurrentProjectView}
            setCurrentView={setCurrentView}
          />
        );
      case 'module_library':
        return (
          <ModuleLibraryPage
            setCurrentView={setCurrentView}
            setCurrentProjectView={setCurrentProjectView}
          />
        );
      case 'search':
        return (
          <GlobalSearchPage
            setCurrentView={setCurrentView}
            setCurrentProjectView={setCurrentProjectView}
          />
        );
      case 'settings':
        return <SettingsPage />;
      case 'workspace_home':
      default:
        return (
          <WorkspaceHome
            setCurrentView={setCurrentView}
            setCurrentProjectView={setCurrentProjectView}
          />
        );
    }
  };

  return (
    <AppShell
      currentView={currentView}
      setCurrentView={setCurrentView}
      currentProjectView={currentProjectView}
      setCurrentProjectView={setCurrentProjectView}
    >
      {renderCurrentView()}
    </AppShell>
  );
};

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <WorkspaceProvider>
          <ModuleProvider>
            <ToastProvider>
              <MainAppRouter />
            </ToastProvider>
          </ModuleProvider>
        </WorkspaceProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
